import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { VendorInvoice, MeterInvoiceData } from 'types/invoices';
import { NspCoreInfo } from 'types/nspCoreInfo';
import { loadImageAsBase64 } from './imageUtils';
import { formatCurrency, formatRate, formatWholeNumber } from './formatters';
import { InvoiceMetadata } from './types';

/**
 * Render the reviewed box - handles its own internal layout
 * @returns The height of the rendered component
 */
export const renderReviewedBox = (
  doc: jsPDF,
  primaryIssuer: NspCoreInfo | null,
  xPosition: number,
  yPosition: number,
  approverName?: string | null,
  approvedAt?: string | null,
): number => {
  const reviewerName = approverName || primaryIssuer?.approverName;
  if (!reviewerName) return 0;

  const boxWidth = 85;
  const boxHeight = 18;

  doc.setFillColor(230, 240, 255);
  doc.roundedRect(xPosition, yPosition, boxWidth, boxHeight, 2, 2, 'F');

  doc.setDrawColor(0, 0, 128);
  doc.setLineWidth(0.5);
  doc.roundedRect(xPosition, yPosition, boxWidth, boxHeight, 2, 2);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 128);
  doc.text('REVIEWED', xPosition + 3, yPosition + 7);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(0, 0, 0);
  let parsedDate: Date | null = null;
  if (approvedAt) {
    if (/^\d+$/.test(approvedAt)) {
      parsedDate = new Date(Number(approvedAt));
    } else {
      const normalized = approvedAt.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
      parsedDate = new Date(normalized);
    }
  }
  const isValidDate = parsedDate !== null && !isNaN(parsedDate.getTime());
  const reviewText = isValidDate
    ? `By ${reviewerName} at ${parsedDate!.toLocaleString('en-US', {
      timeZone: 'America/Halifax',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`
    : `By ${reviewerName}`;
  doc.text(reviewText, xPosition + 3, yPosition + 13);

  return boxHeight;
};

/**
 * Render the NSP use only box - handles its own internal layout
 * @returns The height of the rendered component
 */
export const renderNspUseOnlyBox = (
  doc: jsPDF,
  vendor: VendorInvoice,
  xPosition: number,
  yPosition: number
): number => {
  const boxWidth = 81;
  const minHeight = 38;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(xPosition, yPosition, boxWidth, minHeight);

  doc.setTextColor(255, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('FOR NSP USE ONLY', xPosition + 3, yPosition + 5);

  doc.setFont('helvetica', 'normal');
  doc.text('PO:', xPosition + 3, yPosition + 11);
  doc.text(vendor.vendor_purchase_order_no || 'N/A', xPosition + 20, yPosition + 11);

  doc.text('REC:', xPosition + 3, yPosition + 16);

  let yOffset = 23;
  if (vendor.ipp_wind_net_cost !== null && vendor.ipp_wind_net_cost !== undefined) {
    doc.text(`IPP Wind: ${formatCurrency(vendor.ipp_wind_net_cost)}`, xPosition + 3, yPosition + yOffset);
    yOffset += 5;
  }
  if (vendor.ipp_solar_net_cost !== null && vendor.ipp_solar_net_cost !== undefined) {
    doc.text(`IPP Solar: ${formatCurrency(vendor.ipp_solar_net_cost)}`, xPosition + 3, yPosition + yOffset);
    yOffset += 5;
  }
  if (vendor.comfit_wind_net_cost !== null && vendor.comfit_wind_net_cost !== undefined) {
    doc.text(`COMFIT Wind: ${formatCurrency(vendor.comfit_wind_net_cost)}`, xPosition + 3, yPosition + yOffset);
    yOffset += 5;
  }
  if (vendor.comfit_solar_net_cost !== null && vendor.comfit_solar_net_cost !== undefined) {
    doc.text(`COMFIT Solar: ${formatCurrency(vendor.comfit_solar_net_cost)}`, xPosition + 3, yPosition + yOffset);
    yOffset += 5;
  }
  if (vendor.maxcap_wind_net_cost !== null && vendor.maxcap_wind_net_cost !== undefined) {
    doc.text(`MAXCAP Wind: ${formatCurrency(vendor.maxcap_wind_net_cost)}`, xPosition + 3, yPosition + yOffset);
    yOffset += 5;
  }
  if (vendor.maxcap_solar_net_cost !== null && vendor.maxcap_solar_net_cost !== undefined) {
    doc.text(`MAXCAP Solar: ${formatCurrency(vendor.maxcap_solar_net_cost)}`, xPosition + 3, yPosition + yOffset);
  }

  return minHeight;
};

/**
 * Add logo to the PDF - handles its own internal layout
 * @returns The height of the rendered component
 */
export const addLogoToPDF = async (
  doc: jsPDF,
  logoUrl: string | undefined,
  xPosition: number,
  yPosition: number
): Promise<number> => {
  if (!logoUrl) return 0;

  const logoWidth = 50;
  const logoHeight = 20;

  try {
    const logoBase64 = await loadImageAsBase64(logoUrl);
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', xPosition, yPosition, logoWidth, logoHeight);
      return logoHeight;
    }
  } catch (error) {
    console.error('Failed to add logo to PDF:', error);
  }

  return 0;
};

/**
 * Render vendor information (REMIT TO section) - handles its own internal layout
 * @returns The height of the rendered component
 */
export const renderVendorInfo = (
  doc: jsPDF,
  vendor: VendorInvoice,
  xPosition: number,
  yPosition: number
): number => {
  const lineHeight = 5;
  let currentY = yPosition;

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.text('REMIT TO:', xPosition, currentY);
  currentY += lineHeight;

  doc.setFont('helvetica', 'normal');
  doc.text(vendor.vendor_name, xPosition, currentY);
  currentY += lineHeight;

  if (vendor.vendor_address) {
    doc.text(vendor.vendor_address, xPosition, currentY);
    currentY += lineHeight;
  }

  if (vendor.vendor_email_id) {
    doc.text(`Email: ${vendor.vendor_email_id}`, xPosition, currentY);
    currentY += lineHeight;
  }

  if (vendor.vendor_email_id1) {
    doc.text(`Email: ${vendor.vendor_email_id1}`, xPosition, currentY);
    currentY += lineHeight;
  }

  if (vendor.vendor_email_id2) {
    doc.text(`Email: ${vendor.vendor_email_id2}`, xPosition, currentY);
    currentY += lineHeight;
  }

  if (vendor.vendor_email_id3) {
    doc.text(`Email: ${vendor.vendor_email_id3}`, xPosition, currentY);
    currentY += lineHeight;
  }

  if (vendor.vendor_contact_name) {
    doc.text(`ATTENTION: ${vendor.vendor_contact_name}`, xPosition, currentY);
    currentY += lineHeight;
  }

  return currentY - yPosition + lineHeight;
};

/**
 * Render invoice details box - handles its own internal layout
 * @returns The height of the rendered component
 */
export const renderInvoiceDetailsBox = (
  doc: jsPDF,
  vendor: VendorInvoice,
  xPosition: number,
  yPosition: number,
  metadata: InvoiceMetadata,
  monthYear: string
): number => {
  const boxWidth = 81;
  const boxHeight = 25;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(xPosition, yPosition, boxWidth, boxHeight);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('PO#', xPosition + 3, yPosition + 6);
  doc.text(vendor.vendor_purchase_order_no || 'N/A', xPosition + 45, yPosition + 6);

  doc.text('Invoice No:', xPosition + 3, yPosition + 12);
  doc.text(metadata.invoiceNo, xPosition + 45, yPosition + 12);

  doc.text('Bill Date:', xPosition + 3, yPosition + 18);
  doc.text(metadata.billDate, xPosition + 45, yPosition + 18);

  doc.text('Month Of:', xPosition + 3, yPosition + 24);
  doc.text(monthYear, xPosition + 45, yPosition + 24);

  return boxHeight;
};

/**
 * Render the main meter table
 */
export const renderMeterTable = (
  doc: jsPDF,
  vendor: VendorInvoice,
  yPosition: number
): number => {
  const hasAgcMeters = vendor.meters.some(
    (m: MeterInvoiceData) => m.meter_type?.toLowerCase() === 'agc wind farm'
  );

  // Order: Meter# | Detail | Source | Net Gen kWh | [AGC Net Gen kWh] | kWh Adj | Rate | Net Gen Cost | [AGC Cost] | Dollar Adj | Total Cost
  const headers = ['Meter#', 'Detail', 'Source', 'Net Gen kWh'];
  if (hasAgcMeters) headers.push('AGC Net Gen kWh');
  headers.push('kWh Adj', 'Rate', 'Net Gen Cost');
  if (hasAgcMeters) headers.push('AGC Cost');
  headers.push('Dollar Adj', 'Total Cost');

  const isCounterpartyInvoice = Boolean(vendor.is_counterparty_invoice);
  const columnCount = headers.length;
  const rateColumnIndex = headers.indexOf('Rate');
  const adjustmentRowIndices: number[] = [];

  const tableData: string[][] = [];
  vendor.meters.forEach((meter: MeterInvoiceData) => {
    // agcCost here is display-only (separate "AGC Cost" column below) — dollar_value is
    // the backend source of truth and already includes it, so Total Cost must not re-add it.
    const agcCost =
      meter.agc_energy_kwh != null && meter.rate != null
        ? meter.agc_energy_kwh * meter.rate
        : null;
    const totalCost = meter.dollar_value ?? 0;

    const row = [
      meter.meter_number || '—',
      meter.location || '—',
      meter.meter_read_type ?? 'Actual',
      formatWholeNumber(meter.post_prod_stat_netgen),
    ];
    if (hasAgcMeters) row.push(meter.agc_energy_kwh != null ? formatWholeNumber(meter.agc_energy_kwh) : '—');
    row.push(
      formatWholeNumber(meter.kwh_adjusted),
      meter.rate?.toFixed(3) ?? '—',
      formatCurrency(meter.prod_stat_cost_original),
    );
    if (hasAgcMeters) row.push(agcCost != null ? formatCurrency(agcCost) : '—');
    row.push(formatCurrency(meter.adjustments), formatCurrency(totalCost));
    tableData.push(row);

    // Co-owned meter fee-adjustment lines (Admin Fee, Forecast Fee, CIB/Counterparty share on a
    // Primary invoice; the inverse Primary-Vendor-share line on a Counterparty invoice) — shown
    // as their own blank row directly under the meter, label in "Detail" and amount in the
    // rightmost "Total Cost" column, matching the on-screen Invoice Amount panel.
    const adjustmentLines = isCounterpartyInvoice
      ? [
        meter.primary_vendor_share_amount != null && meter.primary_vendor_share_label
          ? { label: meter.primary_vendor_share_label, amount: meter.primary_vendor_share_amount, rate: null as number | null }
          : null,
        meter.prior_period_adjustment_amount != null && meter.prior_period_adjustment_amount !== 0
          && meter.prior_period_adjustment_label
          ? { label: meter.prior_period_adjustment_label, amount: meter.prior_period_adjustment_amount, rate: null as number | null }
          : null,
      ]
      : [
        meter.counter_party_amount != null && meter.counter_party_label
          // Percentage is already shown inline in the label (e.g. "NSPI Share of Revenue - 20%"),
          // so the Rate column is intentionally left blank here.
          ? { label: meter.counter_party_label, amount: meter.counter_party_amount, rate: null as number | null }
          : null,
        meter.forecast_fee_amount != null && meter.forecast_fee_amount !== 0 && meter.forecast_fee_label
          ? { label: meter.forecast_fee_label, amount: meter.forecast_fee_amount, rate: null as number | null }
          : null,
        meter.admin_fee_amount != null && meter.admin_fee_amount !== 0
          && meter.admin_fee_rate != null && meter.admin_fee_rate !== 0 && meter.admin_fee_label
          ? { label: meter.admin_fee_label, amount: meter.admin_fee_amount, rate: meter.admin_fee_rate ?? null }
          : null,
      ];

    adjustmentLines
      .filter((line): line is { label: string; amount: number; rate: number | null } => line !== null)
      .forEach((line) => {
        const blankRow = new Array(columnCount).fill('');
        blankRow[1] = line.label;
        if (line.rate != null && rateColumnIndex >= 0) blankRow[rateColumnIndex] = formatRate(line.rate);
        blankRow[columnCount - 1] = formatCurrency(line.amount);
        tableData.push(blankRow);
        adjustmentRowIndices.push(tableData.length - 1);
      });
  });

  // AGC layout: 11 columns; non-AGC layout: 9 columns
  // Total usable width: 182mm (A4 210mm − 14mm left − 14mm right margins).
  // AGC layout (11 cols): 11+20+11+16+16+14+14+20+20+20+20 = 182mm
  // Detail widened for location names; kWh Adj widened for "1,234,567"-style values;
  // currency cols trimmed to 20mm (fits "$10,000,000.00" at 7pt with default padding).
  const columnStyles: Record<number, object> = hasAgcMeters
    ? {
      0: { cellWidth: 11, halign: 'left' },   // Meter#
      1: { cellWidth: 20, halign: 'left' },   // Detail (location)
      2: { cellWidth: 11, halign: 'center' }, // Source
      3: { cellWidth: 16, halign: 'right' },  // Net Gen kWh
      4: { cellWidth: 16, halign: 'right' },  // AGC Net Gen kWh
      5: { cellWidth: 14, halign: 'right' },  // kWh Adj
      6: { cellWidth: 14, halign: 'right' },  // Rate — widened to fit fee-adjustment rows'
      // "($0.0030)"-style values on one line
      7: { cellWidth: 20, halign: 'right' },  // Net Gen Cost
      8: { cellWidth: 20, halign: 'right' },  // AGC Cost
      9: { cellWidth: 20, halign: 'right' },  // Dollar Adj
      10: { cellWidth: 20, halign: 'right' }, // Total Cost — widened to fit negative
      // "($123,456.78)"-style values (e.g. NSPI
      // counterparty share) on one line
    }
    : {
      0: { cellWidth: 14, halign: 'left' },   // Meter#
      1: { cellWidth: 22, halign: 'left' },   // Detail
      2: { cellWidth: 15, halign: 'center' }, // Source
      3: { cellWidth: 20, halign: 'right' },  // Net Gen kWh
      4: { cellWidth: 11, halign: 'right' },  // kWh Adj
      5: { cellWidth: 17, halign: 'right' },  // Rate — widened to fit fee-adjustment rows'
      // "($0.0030)"-style values on one line
      6: { cellWidth: 27, halign: 'right' },  // Net Gen Cost
      7: { cellWidth: 27, halign: 'right' },  // Dollar Adj
      8: { cellWidth: 29, halign: 'right' },  // Total Cost
    };

  autoTable(doc, {
    startY: yPosition,
    head: [headers],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: 0,
      fontStyle: 'bold',
      fontSize: hasAgcMeters ? 7 : 8,
      halign: 'left',
    },
    bodyStyles: {
      fontSize: 7,
    },
    columnStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && adjustmentRowIndices.includes(data.row.index)) {
        data.cell.styles.fontStyle = 'italic';
        data.cell.styles.textColor = [80, 80, 80];
      }
    },
  });

  return (doc as any).lastAutoTable.finalY + 5;
};

/**
 * Render the summary table (SUBTOTAL, HST, TOTAL)
 */
export const renderSummaryTable = (
  doc: jsPDF,
  yPosition: number,
  meterSubtotal: number,
  subtotal: number,
  hstValue: number,
  total: number,
  hstRate: number,
  applyHst: boolean = true,
  miscAmount: number = 0,
  miscHstApplicable: boolean = false,
  miscReason: string = ''
): number => {
  // When HST isn't applied, the TOTAL row must fall back to the pre-HST subtotal rather than
  // the HST-inclusive `total`, matching the original (pre-misc-charge) behaviour here.
  const displayTotal = applyHst ? total : subtotal;
  const rows: string[][] = [];
  const miscLabel = miscReason ? `MISCELLANEOUS CHARGE (${miscReason})` : 'MISCELLANEOUS CHARGE';
  if (miscAmount && miscHstApplicable) {
    rows.push(['METER COSTS SUBTOTAL', '', formatCurrency(meterSubtotal)]);
    rows.push([miscLabel, '', formatCurrency(miscAmount)]);
    rows.push(['SUBTOTAL', '', formatCurrency(subtotal)]);
    if (applyHst) rows.push([`HST (${hstRate}%)`, '', formatCurrency(hstValue)]);
    rows.push(['TOTAL', '', formatCurrency(displayTotal)]);
  } else if (miscAmount) {
    rows.push(['SUBTOTAL', '', formatCurrency(meterSubtotal)]);
    if (applyHst) rows.push([`HST (${hstRate}%)`, '', formatCurrency(hstValue)]);
    rows.push([miscLabel, '', formatCurrency(miscAmount)]);
    rows.push(['TOTAL', '', formatCurrency(displayTotal)]);
  } else {
    rows.push(['SUBTOTAL', '', formatCurrency(subtotal)]);
    if (applyHst) rows.push([`HST (${hstRate}%)`, '', formatCurrency(hstValue)]);
    rows.push(['TOTAL', '', formatCurrency(displayTotal)]);
  }
  autoTable(doc, {
    startY: yPosition,
    body: rows,
    theme: 'plain',
    bodyStyles: {
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 130, fontStyle: 'bold', halign: 'left' },
      1: { cellWidth: 15 },
      2: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 10;
};

/**
 * Render the detailed breakdown table - handles its own internal layout
 * @returns The height of the rendered component (via autoTable's finalY)
 */
export const renderDetailedBreakdownTable = (
  doc: jsPDF,
  vendor: VendorInvoice,
  yPosition: number,
  subtotal: number,
  totalNetGen: number
): number => {
  const detailedData = vendor.meters.map((meter: MeterInvoiceData) => [
    `${vendor.vendor_name} - ${meter.location || meter.meter_number}`,
    meter.meter_number || '',
    formatWholeNumber(meter.post_prod_stat_netgen),
    meter.rate?.toFixed(3) ?? '',
    formatCurrency(meter.dollar_value),
    meter.meter_read_type ?? 'Read',
  ]);

  detailedData.push([
    'Grand Total',
    '',
    formatWholeNumber(totalNetGen),
    '',
    formatCurrency(subtotal),
    '',
  ]);

  autoTable(doc, {
    startY: yPosition,
    head: [['Row Labels', 'Meter', 'Net Generation', 'Rate', 'Sum of Total Cost', '']],
    body: detailedData,
    theme: 'grid',
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: 0,
      fontStyle: 'bold',
      fontSize: 8,
    },
    bodyStyles: {
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 70, halign: 'left' },
      1: { cellWidth: 25, halign: 'left' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 15, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 20, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.row.index === detailedData.length - 1) {
        data.cell.styles.fillColor = [173, 216, 230];
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  return (doc as any).lastAutoTable.finalY - yPosition;
};

/**
 * Render the adjustment comments section at the bottom of the invoice.
 * Only shown when at least one meter has a comment on a kWh or Dollar adjustment.
 */
export const renderAdjustmentComments = (
  doc: jsPDF,
  vendor: VendorInvoice,
  yPosition: number
): number => {
  interface CommentRow {
    meterNumber: string;
    field: string;
    comment: string;
    amount: string;
  }

  const rows: CommentRow[] = [];

  vendor.meters.forEach((meter: MeterInvoiceData) => {
    if (meter.kwh_adjustment_comments) {
      rows.push({
        meterNumber: meter.meter_number || '—',
        field: 'kWh Adjustment',
        comment: meter.kwh_adjustment_comments,
        amount: '',
      });
    }
    if (meter.dollar_adjustment_comments) {
      rows.push({
        meterNumber: meter.meter_number || '—',
        field: 'Dollar Adjustment',
        comment: meter.dollar_adjustment_comments,
        amount: '',
      });
    }
    if (meter.reason_for_adjustment) {
      rows.push({
        meterNumber: meter.meter_number || '—',
        field: 'Dollar Adjustment (Reason)',
        comment: meter.reason_for_adjustment,
        amount: '',
      });
    }
  });

  // Misc charges are vendor-level, so the meter number is intentionally blank.
  const miscAmount = vendor.misc_charge?.amount ?? 0;
  if (vendor.misc_charge?.reason || miscAmount) {
    rows.push({
      meterNumber: '',
      field: 'Miscellaneous Charge',
      comment: vendor.misc_charge?.reason ?? '',
      amount: formatCurrency(miscAmount),
    });
  }

  if (rows.length === 0) return yPosition;

  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 15;
  // If not enough room for header + at least one row, add a page
  if (yPosition > pageHeight - bottomMargin - 30) {
    doc.addPage();
    yPosition = 15;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('COMMENTS', 14, yPosition + 5);
  yPosition += 10;

  autoTable(doc, {
    startY: yPosition,
    head: [['Meter Number', 'Adjustment Type', 'Comments / Reason', 'Amount']],
    body: rows.map(r => [r.meterNumber, r.field, r.comment, r.amount]),
    theme: 'grid',
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: 0,
      fontStyle: 'bold',
      fontSize: 8,
    },
    bodyStyles: {
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 22, halign: 'left' },
      1: { cellWidth: 38, halign: 'left' },
      2: { cellWidth: 92, halign: 'left' },
      3: { cellWidth: 30, halign: 'right' },
    },
  });

  return (doc as any).lastAutoTable.finalY + 5;
};

/**
 * Render a "DRAFT" watermark diagonally across the page
 */
export const renderDraftWatermark = (doc: jsPDF): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Save current graphics state
  const currentFontSize = doc.getFontSize();
  const currentTextColor = doc.getTextColor();
  const currentFont = doc.getFont();

  // Set watermark properties
  doc.setFontSize(100);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 180, 180);

  // Calculate center position
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;

  // Save the current transformation matrix and apply low opacity
  doc.saveGraphicsState();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  doc.setGState(new (doc as any).GState({ opacity: 0.15 }));

  // Move to center, rotate, and draw text
  doc.text('DRAFT', centerX, centerY, {
    align: 'center',
    angle: 45, // 45 degree diagonal
  });

  // Restore graphics state
  doc.restoreGraphicsState();

  // Restore original text properties
  doc.setFontSize(currentFontSize);
  doc.setTextColor(currentTextColor);
  doc.setFont(currentFont.fontName, currentFont.fontStyle);
};

/**
 * Append page 2 with meter read information table
 */
export const renderMeterReadsPage = (
  doc: jsPDF,
  vendor: VendorInvoice,
  monthYear: string
): void => {
  doc.addPage();

  const leftMargin = 14;

  // Format month-year label e.g. "2026-05" → "May 2026"
  let monthLabel = monthYear;
  if (monthYear && monthYear.length === 7) {
    const [year, month] = monthYear.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    monthLabel = date.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
  }

  // Page 2 header
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(
    `Page 2 - Invoice for ${vendor.vendor_name} ${monthLabel}`,
    leftMargin,
    20
  );

  const tableData = vendor.meters.map((meter: MeterInvoiceData) => {
    // SCADA and Estimate records show no Read Date, Month, or Year at all — only
    // non-SCADA/Estimate meters get the actual Read Date (never the generic monthYear).
    const blankDate = meter.meter_read_type === 'SCADA' || meter.meter_read_type === 'Estimate';
    return [
      meter.meter_number ?? '—',
      meter.location ?? '—',
      meter.meter_read_type ?? '—',
      blankDate ? '' : (meter.read_date ?? ''),
    ];
  });

  autoTable(doc, {
    startY: 28,
    head: [['Meter #', 'Location', 'Source', 'Date']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: 0,
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 30, halign: 'left' },
      1: { cellWidth: 80, halign: 'left' },
      2: { cellWidth: 40, halign: 'left' },
      3: { cellWidth: 32, halign: 'left' },
    },
  });
};
